import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import {
  Globe,
  ExternalLink,
  Loader2,
  CheckCircle2,
  XCircle,
  X,
  Pencil,
  Copy,
} from 'lucide-react'
import type { Project } from '@/types/conversations'
import { csrfHeaders } from '@/lib/csrf'

const MIN_LENGTH = 6
const MAX_LENGTH = 30
const FORMAT_REGEX = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/

interface VanitySubdomainSectionProps {
  project: Project
}

export function VanitySubdomainSection({ project }: VanitySubdomainSectionProps) {
  const { t } = useTranslation('conversations')
  const [input, setInput] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [availability, setAvailability] = useState<{ available: boolean; error?: string } | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isDeployed = project.production_status === 'deployed'
  const hasVanity = !!project.vanity_subdomain

  // Reset state when leaving edit mode
  useEffect(() => {
    if (!isEditing) {
      setInput('')
      setAvailability(null)
    }
  }, [isEditing])

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const clientValidate = (value: string): string | null => {
    if (value.length < MIN_LENGTH) return t('vanitySubdomain.minLength', { min: MIN_LENGTH })
    if (value.length > MAX_LENGTH) return t('vanitySubdomain.maxLength', { max: MAX_LENGTH })
    if (!FORMAT_REGEX.test(value)) return t('vanitySubdomain.invalidFormat')
    return null
  }

  const checkAvailability = useCallback(async (subdomain: string) => {
    const clientError = clientValidate(subdomain)
    if (clientError) {
      setAvailability({ available: false, error: clientError })
      setIsChecking(false)
      return
    }

    setIsChecking(true)
    try {
      const response = await fetch(`/projects/${project.id}/vanity_subdomain/check?subdomain=${encodeURIComponent(subdomain)}`)
      const data = await response.json()
      setAvailability({ available: data.available, error: data.error })
    } catch {
      setAvailability({ available: false, error: t('vanitySubdomain.networkError') })
    } finally {
      setIsChecking(false)
    }
  }, [project.id, t])

  const handleInputChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
    setInput(sanitized)
    setAvailability(null)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (sanitized.length >= MIN_LENGTH) {
      debounceRef.current = setTimeout(() => {
        checkAvailability(sanitized)
      }, 500)
    }
  }

  const handleClaim = async () => {
    if (!input.trim()) return

    setIsClaiming(true)
    try {
      const response = await fetch(`/projects/${project.id}/vanity_subdomain`, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ subdomain: input.trim() }),
      })
      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(t('vanitySubdomain.claimSuccess'))
        setIsEditing(false)
        router.reload({ only: ['project'] })
      } else {
        const errorMsg = data.errors?.join(', ') || t('vanitySubdomain.claimError')
        toast.error(errorMsg)
      }
    } catch {
      toast.error(t('vanitySubdomain.networkError'))
    } finally {
      setIsClaiming(false)
    }
  }

  const handleRemove = async () => {
    setIsRemoving(true)
    try {
      const response = await fetch(`/projects/${project.id}/vanity_subdomain`, {
        method: 'DELETE',
        headers: csrfHeaders(),
      })
      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(t('vanitySubdomain.removeSuccess'))
        router.reload({ only: ['project'] })
      } else {
        const errorMsg = data.errors?.join(', ') || t('vanitySubdomain.removeError')
        toast.error(errorMsg)
      }
    } catch {
      toast.error(t('vanitySubdomain.networkError'))
    } finally {
      setIsRemoving(false)
    }
  }

  if (!isDeployed) {
    return null
  }

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('vanitySubdomain.title')}
        </h4>
      </div>

      {hasVanity && !isEditing ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-md">
            <code className="text-sm font-mono text-gray-900 dark:text-gray-100 flex-1 truncate">
              {project.vanity_subdomain}.cayu.app
            </code>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() => {
                navigator.clipboard.writeText(`https://${project.vanity_subdomain}.cayu.app`)
                toast.success('Copied!')
              }}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <a
              href={`https://${project.vanity_subdomain}.cayu.app`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ExternalLink className="h-3.5 w-3.5 text-gray-500" />
            </a>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="h-3 w-3 mr-1" />
              {t('vanitySubdomain.change')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs text-red-600 hover:text-red-700 dark:text-red-400"
              onClick={handleRemove}
              disabled={isRemoving}
            >
              {isRemoving ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <X className="h-3 w-3 mr-1" />
              )}
              {isRemoving ? t('vanitySubdomain.removing') : t('vanitySubdomain.remove')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('vanitySubdomain.description')}
          </p>
          <div className="flex items-center gap-1 max-w-xs">
            <Input
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={t('vanitySubdomain.placeholder')}
              className="text-sm font-mono h-8"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && availability?.available && !isClaiming) {
                  handleClaim()
                }
              }}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap font-mono">
              {t('vanitySubdomain.suffix')}
            </span>
          </div>

          {/* Availability feedback */}
          {input.length >= MIN_LENGTH && (
            <div className="flex items-center gap-1.5">
              {isChecking ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                  <span className="text-xs text-gray-500">{t('vanitySubdomain.checking')}</span>
                </>
              ) : availability?.available ? (
                <>
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  <span className="text-xs text-green-600 dark:text-green-400">{t('vanitySubdomain.available')}</span>
                </>
              ) : availability?.error ? (
                <>
                  <XCircle className="h-3 w-3 text-red-500" />
                  <span className="text-xs text-red-600 dark:text-red-400">{availability.error}</span>
                </>
              ) : null}
            </div>
          )}

          {/* Client-side validation for short input */}
          {input.length > 0 && input.length < MIN_LENGTH && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">{t('vanitySubdomain.minLength', { min: MIN_LENGTH })}</span>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleClaim}
              disabled={!availability?.available || isClaiming}
            >
              {isClaiming ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  {t('vanitySubdomain.claiming')}
                </>
              ) : (
                t('vanitySubdomain.claim')
              )}
            </Button>
            {isEditing && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                {t('common:cancel')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
